import React, { useState, useEffect } from 'react';

const HomeImageSlider = ({ images, title }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [visibleItems, setVisibleItems] = useState(4);

  // Responsive visible items
  useEffect(() => {
    const updateVisibleItems = () => {
      const width = window.innerWidth;
      if (width < 480) {
        setVisibleItems(1);
      } else if (width < 768) {
        setVisibleItems(2);
      } else if (width < 1024) {
        setVisibleItems(3);
      } else {
        setVisibleItems(4);
      }
    };

    updateVisibleItems();
    window.addEventListener('resize', updateVisibleItems);
    return () => window.removeEventListener('resize', updateVisibleItems);
  }, []);

  const maxIndex = Math.max(0, images.length - visibleItems);

  // Auto slide functionality - moves one image at a time
  useEffect(() => {
    if (!isHovered && images.length > visibleItems) {
      const interval = setInterval(() => {
        setCurrentIndex((prevIndex) => {
          if (prevIndex >= maxIndex) {
            return 0; // Reset to beginning
          }
          return prevIndex + 1;
        });
      }, 3000); // Move one image every 3 seconds

      return () => clearInterval(interval);
    }
  }, [images.length, visibleItems, maxIndex, isHovered]);

  // Manual navigation functions - move one image at a time
  const goToPrevious = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? maxIndex : prevIndex - 1
    );
  };

  const goToNext = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex >= maxIndex ? 0 : prevIndex + 1
    );
  };

  const goToSlide = (index) => {
    setCurrentIndex(Math.min(index, maxIndex));
  };

  if (!images || images.length === 0) {
    return null;
  }

  return (
    <div 
      className="home-image-slider"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        position: 'relative',
        width: '100%',
        maxWidth: '1200px',
        margin: '0 auto',
        borderRadius: '12px',
        overflow: 'hidden'
      }}
    >
             {/* Main slider container */}
       <div 
         style={{
           position: 'relative',
           width: '100%',
           height: '225px', // Reduced from 300px to 225px (25% smaller)
           overflow: 'hidden'
         }}
       >
         {/* Images */}
         <div 
           style={{
             display: 'flex',
             height: '100%',
             transform: `translateX(-${currentIndex * (100 / visibleItems)}%)`,
             transition: 'transform 0.5s ease-in-out',
             gap: '15px',
             paddingLeft: '10px',
             paddingRight: '10px'
           }}
         >
           {images.map((imageUrl, index) => (
             <div
               key={index}
               style={{
                 flex: `0 0 calc((100% - ${(visibleItems - 1) * 15}px) / ${visibleItems})`,
                 height: '100%',
                 borderRadius: '8px',
                 overflow: 'hidden'
               }}
             >
               <img
                 src={imageUrl}
                 alt={`${title || 'Gallery'} image ${index + 1}`}
                 style={{
                   width: '100%',
                   height: '100%',
                   objectFit: 'cover',
                   borderRadius: '0',
                   transition: 'transform 0.3s ease'
                 }}
                 onMouseEnter={(e) => {
                   e.target.style.transform = 'scale(1.05)';
                 }}
                 onMouseLeave={(e) => {
                   e.target.style.transform = 'scale(1)';
                 }}
               />
             </div>
           ))}
         </div>

                 {/* Navigation arrows - only show if more images than visible */}
         {images.length > visibleItems && (
          <>
            <button
              onClick={goToPrevious}
              style={{
                position: 'absolute',
                left: '20px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '50px',
                height: '50px',
                fontSize: '20px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background 0.3s ease',
                zIndex: 10
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(0, 0, 0, 0.8)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(0, 0, 0, 0.5)';
              }}
            >
              &#8249;
            </button>

            <button
              onClick={goToNext}
              style={{
                position: 'absolute',
                right: '20px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '50px',
                height: '50px',
                fontSize: '20px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background 0.3s ease',
                zIndex: 10
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(0, 0, 0, 0.8)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(0, 0, 0, 0.5)';
              }}
            >
              &#8250;
            </button>
          </>
        )}

                 {/* Auto-slide indicator */}
         {!isHovered && images.length > visibleItems && (
          <div
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              background: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              padding: '5px 10px',
              borderRadius: '15px',
              fontSize: '12px',
              zIndex: 10
            }}
          >
            Auto-sliding
          </div>
        )}
      </div>

             {/* Dots indicator - one dot per possible starting position */}
       {images.length > visibleItems && (
         <div
           style={{
             display: 'flex',
             justifyContent: 'center',
             alignItems: 'center',
             padding: '20px 0',
             gap: '10px'
           }}
         >
           {Array.from({ length: maxIndex + 1 }).map((_, index) => (
             <button
               key={index}
               onClick={() => goToSlide(index)}
               style={{
                 width: '12px',
                 height: '12px',
                 borderRadius: '50%',
                 border: 'none',
                 background: index === currentIndex ? '#007bff' : '#ccc',
                 cursor: 'pointer',
                 transition: 'background 0.3s ease',
                 outline: 'none'
               }}
               onMouseEnter={(e) => {
                 if (index !== currentIndex) {
                   e.target.style.background = '#666';
                 }
               }}
               onMouseLeave={(e) => {
                 if (index !== currentIndex) {
                   e.target.style.background = '#ccc';
                 }
               }}
             />
           ))}
         </div>
       )}

             {/* Progress bar */}
       {!isHovered && images.length > visibleItems && (
         <div
           style={{
             position: 'absolute',
             bottom: '0',
             left: '0',
             width: '100%',
             height: '3px',
             background: 'rgba(255, 255, 255, 0.3)',
             zIndex: 10
           }}
         >
           <div
             style={{
               height: '100%',
               background: '#007bff',
               width: `${((currentIndex) / maxIndex) * 100}%`,
               transition: 'width 0.5s ease'
             }}
           />
         </div>
       )}

             {/* Info bar showing current position */}
       {images.length > visibleItems && (
         <div
           style={{
             textAlign: 'center',
             padding: '10px 0',
             fontSize: '14px',
             color: '#666'
           }}
         >
           Showing {Math.min(currentIndex + visibleItems, images.length)} of {images.length} images (position {currentIndex + 1})
         </div>
       )}
    </div>
  );
};

export default HomeImageSlider; 